import { useState, useCallback, useRef } from 'react';
import { message } from 'antd';
import type { FormInstance } from 'antd';
import { draftStorage, isIndexedDBAvailable } from '@/utils/draftStorage';
import type { Checklist } from '@/services/types/checklist.types';

const DRAFT_FILE_MARKER = '_isDraftFile';

interface UseChecklistDraftReturn {
  hasDraft: boolean;
  available: boolean;
  loading: boolean;
  saving: boolean;
  saveDraft: () => Promise<void>;
  loadDraft: () => Promise<boolean>;
  clearDraft: () => Promise<void>;
}

/**
 * Hook para gerenciar rascunhos do checklist no IndexedDB.
 * Salva dados textuais + arquivos (fotos/files) localmente no navegador.
 */
export function useChecklistDraft(
  checklistId: string | undefined,
  inspectionId: string | undefined,
  form: FormInstance,
  checklist: Checklist | null,
): UseChecklistDraftReturn {
  const [hasDraft, setHasDraft] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const available = useRef(isIndexedDBAvailable()).current;

  const getFileQuestionIds = useCallback((): Set<string> => {
    if (!checklist) return new Set();
    const ids = new Set<string>();
    for (const group of checklist.groups) {
      for (const section of group.sections) {
        for (const question of section.questions) {
          if (question.answerType === 'Photo' || question.answerType === 'File') {
            ids.add(question.id);
          }
        }
      }
    }
    return ids;
  }, [checklist]);

  const saveDraft = useCallback(async () => {
    if (!available || !checklistId || !inspectionId) return;

    setSaving(true);
    try {
      const values = form.getFieldsValue();
      const fileQuestionIds = getFileQuestionIds();
      const cleanedValues: Record<string, any> = {};

      for (const [key, value] of Object.entries(values)) {
        if (value === undefined || value === null) continue;

        if (fileQuestionIds.has(key) && value && typeof value === 'object') {
          // Campo de foto/arquivo - salvar blobs no IndexedDB separadamente
          const val = value as any;
          const fileList = val.fileList || (Array.isArray(val) ? val : []);
          const fileRefs: any[] = [];

          for (let i = 0; i < fileList.length; i++) {
            const file = fileList[i];

            if (file.originFileObj) {
              // Arquivo novo (ainda nao submetido) - salvar blob no IndexedDB
              await draftStorage.saveChecklistFile(
                inspectionId,
                key,
                i,
                file.originFileObj,
                file.name || file.fileName || `file-${i}`,
              );
              fileRefs.push({
                [DRAFT_FILE_MARKER]: true,
                fileIndex: i,
                fileName: file.name || file.fileName,
                fileType: file.type || file.originFileObj.type,
                fileSize: file.size || file.originFileObj.size,
              });
            } else if (file.documentId || file.url) {
              // Arquivo ja submetido anteriormente (tem documentId do S3)
              fileRefs.push({
                uid: file.uid,
                name: file.name || file.fileName,
                status: 'done',
                url: file.url,
                thumbUrl: file.thumbUrl,
                documentId: file.documentId,
                isFromVideoConference: file.isFromVideoConference,
              });
            }
          }

          cleanedValues[key] = { fileList: fileRefs };
        } else {
          // Campo de texto/numero/selecao - salvar como esta
          cleanedValues[key] = value;
        }
      }

      await draftStorage.saveChecklistDraft(inspectionId, checklistId, cleanedValues);
      setHasDraft(true);
      message.success('Rascunho salvo!');
    } catch (error) {
      console.error('Erro ao salvar rascunho do checklist:', error);
      message.error('Erro ao salvar rascunho');
    } finally {
      setSaving(false);
    }
  }, [available, checklistId, inspectionId, form, getFileQuestionIds]);

  const loadDraft = useCallback(async (): Promise<boolean> => {
    if (!available || !inspectionId) return false;

    setLoading(true);
    try {
      const draft = await draftStorage.getChecklistDraft(inspectionId);
      if (!draft) {
        setHasDraft(false);
        return false;
      }

      const restoredValues: Record<string, any> = {};
      const fileQuestionIds = getFileQuestionIds();

      for (const [key, value] of Object.entries(draft.formValues)) {
        if (fileQuestionIds.has(key) && value && typeof value === 'object' && (value as any).fileList) {
          // Restaurar campo de arquivo
          const fileList = (value as any).fileList;
          const restoredFileList: any[] = [];

          for (const fileRef of fileList) {
            if (fileRef[DRAFT_FILE_MARKER]) {
              // Arquivo salvo no IndexedDB - restaurar blob
              const files = await draftStorage.getChecklistFiles(inspectionId, key);
              const found = files.find((f) => f.key === `${inspectionId}:${key}:${fileRef.fileIndex}`);
              if (found) {
                const file = new File([found.blob], found.fileName, { type: found.fileType });
                restoredFileList.push({
                  uid: `draft-${key}-${fileRef.fileIndex}`,
                  name: found.fileName,
                  status: 'done',
                  originFileObj: file,
                  thumbUrl: URL.createObjectURL(found.blob),
                  size: found.fileSize,
                  type: found.fileType,
                });
              }
            } else {
              // Arquivo ja submetido (com documentId) - preservar referencia
              restoredFileList.push(fileRef);
            }
          }

          restoredValues[key] = { fileList: restoredFileList };
        } else {
          restoredValues[key] = value;
        }
      }

      form.setFieldsValue(restoredValues);
      setHasDraft(true);
      message.info('Rascunho do checklist carregado');
      return true;
    } catch (error) {
      console.error('Erro ao carregar rascunho do checklist:', error);
      setHasDraft(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, [available, inspectionId, form, getFileQuestionIds]);

  const clearDraft = useCallback(async () => {
    if (!available || !inspectionId) return;
    try {
      await draftStorage.deleteChecklistDraft(inspectionId);
      setHasDraft(false);
    } catch (error) {
      console.error('Erro ao limpar rascunho do checklist:', error);
    }
  }, [available, inspectionId]);

  return {
    hasDraft,
    available,
    loading,
    saving,
    saveDraft,
    loadDraft,
    clearDraft,
  };
}
